'use client';

import styles from './ProgressMeter.module.scss';

import type { ReactNode } from 'react';

export type ProgressMeterTone =
  | 'default'
  | 'success'
  | 'info'
  | 'warning'
  | 'destructive';
export type ProgressMeterSize = 'sm' | 'md' | 'lg';

type ProgressMeterProps = {
  percentage: number;
  label?: ReactNode;
  rightLabel?: ReactNode;
  tone?: ProgressMeterTone;
  size?: ProgressMeterSize;
  className?: string;
  ariaLabel?: string;
};

function clamp(value: number) {
  return Math.max(0, Math.min(100, value));
}

export function ProgressMeter({
  percentage,
  label,
  rightLabel,
  tone = 'default',
  size = 'md',
  className,
  ariaLabel,
}: ProgressMeterProps) {
  const value = clamp(percentage);
  const rootClassName = [
    styles.progressMeter,
    styles[size === 'sm' ? 'sizeSm' : size === 'lg' ? 'sizeLg' : 'sizeMd'],
    styles[
      tone === 'success'
        ? 'toneSuccess'
        : tone === 'info'
          ? 'toneInfo'
          : tone === 'warning'
            ? 'toneWarning'
            : tone === 'destructive'
              ? 'toneDestructive'
              : 'toneDefault'
    ],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClassName} aria-label={ariaLabel}>
      {label || rightLabel ? (
        <div className={styles.progressMeter__labels}>
          <span className={styles.progressMeter__label}>{label}</span>
          <span className={styles.progressMeter__rightLabel}>{rightLabel}</span>
        </div>
      ) : null}
      <div className={styles.progressMeter__track}>
        <div
          className={styles.progressMeter__fill}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}
